import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [forcePasswordReset, setForcePasswordReset] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      // Check if user needs to reset password
      if (response.data.force_password_reset) {
        setForcePasswordReset(true);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { 
      identifier: email, 
      password, 
      login_type: 'email' 
    });
    const { access_token, user: userData, force_password_reset } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setForcePasswordReset(force_password_reset || false);
    return { user: userData, forcePasswordReset: force_password_reset };
  };

  const loginWithCredentials = async (identifier, password, loginType = 'email', siteId = null) => {
    const payload = { 
      identifier, 
      password, 
      login_type: loginType 
    };
    // Include site_id for cross-tenant login validation
    if (siteId) {
      payload.site_id = siteId;
    }
    const response = await axios.post(`${API_URL}/auth/login`, payload);
    const { access_token, user: userData, force_password_reset } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setForcePasswordReset(force_password_reset || false);
    return { user: userData, forcePasswordReset: force_password_reset };
  };

  const changePassword = async (newPassword, currentPassword = null) => {
    const payload = { new_password: newPassword };
    if (currentPassword) {
      payload.current_password = currentPassword;
    }
    await axios.post(`${API_URL}/auth/change-password`, payload);
    setForcePasswordReset(false);
    // Update user object
    setUser(prev => ({ ...prev, force_password_reset: false }));
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Set auth state directly (used for OTP login)
  const setAuthState = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
    setForcePasswordReset(userData?.force_password_reset || false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      loginWithCredentials, 
      register, 
      logout, 
      updateUser, 
      fetchUser,
      forcePasswordReset,
      changePassword,
      setAuthState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
